export const WEBSITE_PACKAGES = [
  {
    slug: "launch",
    name: "Starter",
    price: "A$399",
    priceNote: "from",
    pages: "1 page",
    revisions: "1 revision round",
    payment: "100% upfront",
    bestFor: "You need one page to explain a focused offer and collect enquiries, with your copy and images ready to supply.",
    example: "A sole trader introducing one service, a short business overview and a contact form on a single scrolling page.",
    includes: [
      "One responsive scrolling page",
      "Contact or enquiry form",
      "Mobile optimisation",
      "Basic on-page SEO setup",
      "Client-supplied copy and imagery placed into the design",
      "Launch-ready technical QA",
    ],
  },
  {
    slug: "business",
    name: "Business",
    price: "A$1,499",
    priceNote: "from",
    pages: "Up to 5 pages",
    revisions: "2 revision rounds",
    payment: "50% to begin · 50% once approved, before launch",
    bestFor: "You need separate pages for your services, business information and contact details, within a five-page website.",
    example: "A service business with Home, About, two service pages and Contact, giving each offer room to explain itself.",
    includes: [
      "Custom visual design",
      "Responsive mobile + tablet + laptop + desktop build",
      "Forms, maps and practical integrations",
      "Analytics setup",
      "SEO foundations",
      "Performance and launch QA",
    ],
    featured: true,
  },
  {
    slug: "growth",
    name: "Premium",
    price: "A$2,499",
    priceNote: "from",
    pages: "6–10 pages",
    revisions: "3 revision rounds",
    payment: "40% to begin · 30% at design approval · 30% before launch",
    bestFor: "You need five to ten pages, more detailed service content, SEO and AI-search structure, and richer visual interactions.",
    example: "An established business with several services, dedicated service pages, useful supporting content and a more expressive design.",
    includes: [
      "Premium custom design direction",
      "SEO + AI-search structure",
      "Richer media, motion and interactive features where appropriate",
      "Analytics and conversion-focused structure",
      "Performance optimisation",
      "Detailed launch QA",
    ],
  },
  {
    slug: "custom",
    name: "Custom",
    price: "Quote",
    priceNote: "",
    pages: "Custom scope",
    revisions: "Agreed in proposal",
    payment: "Milestone payment plan agreed before work begins",
    bestFor: "You need more than ten pages, complex integrations, automation or functionality that needs its own technical scope.",
    example: "An online store, a custom booking workflow or a website connected to business systems. Features and any ongoing software fees are scoped in the proposal.",
    includes: [
      "Custom page count and functionality",
      "Integration planning",
      "Technical feasibility review",
      "Advanced interaction or automation scope where required",
      "Clear deliverables and exclusions",
      "Itemised proposal before work begins",
    ],
  },
];

export const SUPPORT_PLANS = [
  {
    slug: "basic-support",
    name: "Basic Website Care",
    price: "A$79",
    cadence: "/ month",
    bestFor: "Businesses that want their website hosted, monitored and kept current with occasional small changes.",
    term: "Month-to-month · cancel with 30 days’ notice",
    includes: [
      "Managed standard website hosting",
      "SSL certificate management and DNS support",
      "Uptime and security monitoring",
      "Backup coverage where supported by the hosting setup",
      "Up to 2 small update requests per month",
      "Text, pricing, service/product, offer and supplied image updates",
      "Reasonable fixes to existing Thrivehaus-delivered code",
      "Initial response target: within 3 South Australia business days",
    ],
  },
  {
    slug: "managed-support",
    name: "Managed Website Care",
    price: "A$129",
    cadence: "/ month",
    bestFor: "Businesses that want hosting plus regular website changes, reporting and a faster support path.",
    term: "Month-to-month · cancel with 30 days’ notice",
    includes: [
      "Everything in Basic Website Care",
      "Up to 5 small update requests per month",
      "Minor layout adjustments within existing pages",
      "Initial response target: within 2 South Australia business days",
      "Monthly website health and enquiry-form check",
      "Monthly analytics and performance report",
      "Basic improvement recommendations",
    ],
    featured: true,
  },
  {
    slug: "growth-support",
    name: "Growth Website Care",
    price: "A$199",
    cadence: "/ month",
    bestFor: "Businesses that want Thrivehaus actively improving content, search visibility and website performance.",
    term: "3-month initial term · then month-to-month · cancel with 30 days’ notice",
    includes: [
      "Everything in Managed Website Care",
      "Up to 10 small update requests per month",
      "Initial response target: within 1 South Australia business day",
      "Basic ongoing SEO improvements",
      "AI-search optimisation",
      "Monthly search visibility reporting",
      "Performance reviews",
      "Detailed monthly improvement recommendations",
    ],
  },
];

export const SERVICE_TERMS = {
  websites: {
    type: "Fixed project agreement",
    term: "No ongoing contract required for the website build itself.",
    payment: "Starter is paid 100% upfront. Business uses 50% to begin and 50% before launch. Premium uses 40% to begin, 30% at design approval and 30% before launch. Custom uses a milestone plan agreed before work begins.",
  },
  seo: {
    type: "One-off or ongoing",
    term: "No minimum term. SEO can be a one-off project or ongoing month-to-month work with 30 days’ notice.",
  },
  "ai-search": {
    type: "One-off or ongoing",
    term: "One-off AI-search work has no minimum term. Ongoing AI-search work follows the term stated in the proposal; Growth Website Care uses a 3-month initial term.",
  },
  automation: {
    type: "Scoped project",
    term: "Project and any ongoing support term are confirmed in the proposal based on the systems, usage costs and maintenance required.",
  },
  "hosting-support": {
    type: "Ongoing website care",
    term: "Basic and Managed are month-to-month with 30 days’ notice. Growth starts with a 3-month initial term, then becomes month-to-month with 30 days’ notice.",
    annual: "Optional annual prepay: receive 12 months of website care for the price of 11.",
    infrastructure: "Standard managed hosting is included in all care plans for standard Thrivehaus business websites. Domain registration, business email, paid platforms, ecommerce fees and unusually resource-intensive or custom infrastructure are scoped separately.",
  },
};

export const SERVICE_PRICE_LABELS = {
  websites: "Website builds from A$399",
  seo: "Scoped quote",
  "ai-search": "Scoped quote",
  "hosting-support": "Website care + hosting from A$79/month",
  automation: "Scoped quote",
};

export const SERVICE_DETAIL_DATA = {
  websites: {
    bestFor: "Businesses that need a professional website that looks custom, works properly across mobile, tablet, laptop and desktop and gives customers a clear reason to enquire.",
    solves: [
      "An outdated or generic website",
      "Poor mobile presentation",
      "Confusing navigation or weak enquiry paths",
      "A business that looks stronger offline than it does online",
    ],
    process: [
      ["01", "Preview", "Start with a free 1-page design preview to see the visual direction before committing to the full website."],
      ["02", "Choose scope", "If you like the preview, choose the package and confirm the full website scope, pages and functionality."],
      ["03", "Build", "Mobile, tablet, laptop and desktop are developed together, then performance and forms are tested."],
      ["04", "Launch", "Approved work is connected, checked and launched with the agreed analytics and SEO foundations."],
    ],
    exclusions: [
      "Domain registration, email hosting and paid third-party subscriptions unless listed in the proposal",
      "Professional photography, video production or long-form copywriting unless separately scoped",
      "Major functionality outside the selected package",
      "Unlimited revisions or ongoing changes after the included revision rounds",
    ],
    faq: [
      ["Can I start with one page and add more later?", "Yes. Starter is deliberately small, and additional pages can be quoted later without forcing you into a larger package upfront."],
      ["Do you use templates?", "The visual direction is customised around the business. Reusable technical components may be used where they improve speed and reliability, but the customer-facing design is not sold as a generic template."],
      ["Does AI make the website cheaper?", "We use modern and AI-assisted tools to reduce repetitive production work. That efficiency is reflected in the pricing, while structure, design decisions, review, QA and accountability remain human-led."],
    ],
  },
  seo: {
    bestFor: "Businesses with a useful website that is not being discovered consistently through organic or local search.",
    solves: [
      "Important pages that are hard for search engines to understand",
      "Weak titles, headings, internal links or local signals",
      "Content that does not match what customers are actually searching for",
      "Technical issues that make crawling or indexing harder than it should be",
    ],
    process: [
      ["01", "Audit", "We review technical health, indexing, page structure and current search signals."],
      ["02", "Prioritise", "We identify the highest-value technical, local and content opportunities."],
      ["03", "Improve", "We update structure, metadata, internal links and content recommendations."],
      ["04", "Refine", "Results and search behaviour are reviewed over time where ongoing work is included."],
    ],
    exclusions: [
      "Guaranteed rankings, traffic or revenue",
      "Paid advertising spend",
      "Large-scale content production unless separately scoped",
      "Third-party SEO software or listing fees unless specifically included",
    ],
    faq: [
      ["Can you guarantee page-one rankings?", "No. No responsible provider can guarantee a specific organic ranking. We improve the technical and content conditions that support stronger visibility."],
      ["Is SEO included in website packages?", "Website packages include foundations appropriate to the package. Deeper ongoing SEO work is scoped separately."],
      ["Do you handle local SEO?", "Yes, where relevant. Service-area structure, local signals and business information can form part of the scope."],
      ["Is there a minimum term for SEO?", "No. SEO can be a one-off project or ongoing month-to-month work with 30 days’ notice."],
    ],
  },
  "ai-search": {
    bestFor: "Businesses that want their services, expertise and answers structured clearly for modern AI-assisted search and answer engines.",
    solves: [
      "Vague service descriptions that machines struggle to interpret",
      "Inconsistent business, service and location information",
      "Important customer questions that are not clearly answered on the site",
      "A website built only for traditional blue-link search",
    ],
    process: [
      ["01", "Clarify", "We map the business entities, services, locations and questions that need to be understood."],
      ["02", "Structure", "Content and machine-readable data are organised consistently."],
      ["03", "Improve", "Important pages are rewritten or reorganised for clearer answers and stronger context."],
      ["04", "Review", "The structure is revisited as AI-search behaviour and the business change."],
    ],
    exclusions: [
      "Guaranteed mentions or citations in AI answers",
      "Fabricated reviews, expertise or authority signals",
      "Large content libraries unless separately scoped",
      "Paid placements in third-party AI products",
    ],
    faq: [
      ["Can you guarantee ChatGPT or Google AI will mention my business?", "No. We can improve clarity and machine readability, but no provider can guarantee inclusion in a generated answer."],
      ["Is this different from SEO?", "Yes, but they overlap. Traditional SEO focuses heavily on search discovery and ranking signals; AI-search work also focuses on entity clarity, answer-ready content and machine-readable context."],
      ["Should a new website include AI-search structure?", "For many businesses, yes. Premium website scopes can include SEO and AI-search foundations from the start."],
    ],
  },
  "hosting-support": {
    bestFor: "Businesses that want one provider accountable for hosting, monitoring, routine website changes and ongoing care after launch.",
    solves: [
      "A site that slowly goes stale after launch",
      "Small updates becoming a hassle",
      "No clear person responsible for website fixes",
      "Hosting, SSL, DNS and monitoring being spread across different providers",
    ],
    process: [
      ["01", "Choose care", "Select Basic, Managed or Growth depending on how actively you want the website maintained and improved."],
      ["02", "Host + protect", "Standard hosting, SSL, DNS support, uptime/security monitoring and applicable backups are managed as part of the care plan."],
      ["03", "Maintain", "Content changes and website fixes are handled within the selected plan’s scope."],
      ["04", "Report + improve", "Managed adds monthly analytics reporting; Growth adds search visibility work and more detailed improvement recommendations."],
    ],
    exclusions: [
      "Domain registration, business email and paid third-party subscriptions unless specifically listed",
      "Ecommerce platform fees, usage-based services or unusually resource-intensive/custom hosting",
      "Unlimited redesign work",
      "Major new pages, features or functionality inside routine care allowances",
    ],
    faq: [
      ["Does A$79/month include hosting?", "Yes. Basic Website Care includes standard managed hosting for a standard Thrivehaus business website, plus SSL/DNS support, uptime and security monitoring, applicable backups, small updates and reasonable fixes."],
      ["What does monthly reporting include?", "Managed Website Care includes a monthly analytics and performance summary with basic recommendations. Growth Website Care also includes search visibility reporting and more detailed improvement recommendations."],
      ["What is a small update request?", "One straightforward change to existing website content, pricing, service or product information, offers, supplied imagery or similar existing-page content. Unused requests do not roll over. New pages, redesigns, complex edits and new functionality are quoted separately."],
      ["How fast is support?", "The plan response times are initial-response targets during South Australia business days, not guaranteed resolution times. Complex work may need a separate scope and timeline."],
      ["Can I cancel care?", "Yes. Basic and Managed are month-to-month with 30 days’ notice. Growth has a 3-month initial term, then becomes month-to-month with 30 days’ notice."],
      ["Is there an annual option?", "Yes. You can prepay annually and receive 12 months of website care for the price of 11."],
    ],
  },
  automation: {
    bestFor: "Businesses losing time to repetitive handoffs between website enquiries, inboxes, bookings, CRMs and internal workflows.",
    solves: [
      "Enquiries being copied manually between systems",
      "Slow or inconsistent lead follow-up",
      "Forms that create admin instead of reducing it",
      "Disconnected booking, CRM, notification or intake tools",
    ],
    process: [
      ["01", "Map", "We identify the current manual steps, systems and failure points."],
      ["02", "Design", "The smallest reliable workflow that removes unnecessary admin is agreed."],
      ["03", "Connect", "APIs, webhooks, forms, notifications and data flows are implemented and tested."],
      ["04", "Document", "The finished workflow is documented so it remains understandable and supportable."],
    ],
    exclusions: [
      "Third-party subscription or usage fees",
      "Unsupported or inaccessible systems",
      "High-risk automation without human approval steps where those are appropriate",
      "Large custom software products unless separately scoped",
    ],
    faq: [
      ["What can you automate?", "Common examples include enquiry routing, booking/intake flows, CRM sync, notifications, follow-up sequences and data movement between web tools."],
      ["How is automation priced?", "By scope. Complexity depends on the number of systems, API availability, logic, testing and ongoing usage costs."],
      ["Will the workflow be documented?", "Yes. Practical documentation is part of the service so the automation does not become a black box."],
      ["Is there a minimum automation contract?", "Automation is scoped project-by-project. Any ongoing support or maintenance term is agreed in the proposal based on the systems and usage involved."],
    ],
  },
};
